// src/ui/organisms/coaEditForm/EditChartOfAccountsForm.tsx
import React, {
    useEffect,
    useMemo,
    useImperativeHandle,
    forwardRef,
} from "react";
import { useForm } from "react-hook-form";

import type { BoxProps } from "@/ui/atoms/Box";
import Box from "@/ui/atoms/Box";
import TextField from "@/ui/atoms/TextField";
import Dropdown from "@/ui/molecules/Dropdown";
import { Checkbox } from "@proda-ai/ui-kit";
import LayoutFillParentCentered from "@/ui/layouts/LayoutFillParentCentered";
import ErrorMessage from "@/ui/atoms/ErrorMessage";

import type {
    TChartOfAccountsDefinition,
    TCompany,
    TNewCoADefinition,
} from "@/apis/odin/apiODIN";

const testIds = {
    checkboxIncludeSubcategories: "checkbox-include-subcategories",
    checkboxIncludeNonOperatingAccounts:
        "checkbox-include-non-operating-accounts",
};

export interface CoAEditFormHandle {
    isDirty: boolean;
    onConfirm: () => Promise<void>;
}

export interface EditCoAFormProps extends BoxProps {
    excludedCoANames: string[];
    companies: TCompany[] | null;
    getCompaniesState: "idle" | "pending" | "rejected" | "resolved";
    coaDefinition: TChartOfAccountsDefinition | null;
    coaContainsNonOperatingAccounts: boolean;
    coaContainsSubcategories: boolean;
    getExcludedCoANames: () => Promise<void>;
    onCoASubmit: (data: TNewCoADefinition, coaId?: number) => Promise<void>;
    onDiscardCoA: (coaId?: number) => void;
    navigateToChartOfAccountsEditDetails: (coaId: number) => void;
    coaId?: number;
    isLoading: boolean;
    isError: boolean;
    onDirtyChange?: (dirty: boolean) => void;
}

const EditChartOfAccountsForm = forwardRef<CoAEditFormHandle, EditCoAFormProps>(
    (
        {
            excludedCoANames,
            companies,
            getCompaniesState,
            coaDefinition,
            coaContainsNonOperatingAccounts,
            coaContainsSubcategories,
            getExcludedCoANames,
            onCoASubmit,
            navigateToChartOfAccountsEditDetails,
            coaId,
            isLoading,
            isError,
            onDirtyChange,
            sx,
            ...props
        },
        ref,
    ) => {
        const coaIsDraft = coaDefinition?.isDraft ?? true;

        const {
            register,
            setError,
            setValue,
            watch,
            getValues,
            formState: { errors, isDirty },
            trigger,
            reset,
        } = useForm<TNewCoADefinition>({
            defaultValues: {
                name: "",
                description: "",
                includeSubcategories: false,
                includeNonOperatingAccounts: false,
                companyId: null,
            },
        });

        useEffect(() => {
            reset({
                name: coaDefinition?.name ?? "",
                description: coaDefinition?.description ?? "",
                includeSubcategories: coaDefinition?.hasSubcategory ?? false,
                includeNonOperatingAccounts:
                    coaDefinition?.hasNonOperatingAccounts ?? false,
                companyId: coaDefinition?.ownerCompanyId ?? null,
            });
        }, [coaDefinition, reset]);

        useEffect(() => {
            register("companyId", { required: "Owner company is required" });
        }, [register]);

        const companyOptions = useMemo(
            () =>
                companies?.map(({ companyId, companyName }) => ({
                    value: companyId,
                    label: companyName,
                })) ?? [],
            [companies],
        );

        const validateCoaName = React.useCallback(
            (value: string) => {
                const trimmed = value.trim().toLowerCase();
                const currentNameLower = coaDefinition?.name
                    ?.trim()
                    .toLowerCase();

                if (
                    excludedCoANames.filter(
                        existing => existing.trim().toLowerCase() === trimmed,
                    ).length > 1
                ) {
                    return true;
                }
                if (trimmed === currentNameLower) {
                    return false;
                }
                return excludedCoANames.some(
                    existing => existing.trim().toLowerCase() === trimmed,
                );
            },
            [coaDefinition, excludedCoANames],
        );

        const onConfirm = React.useCallback(async () => {
            await getExcludedCoANames();
            const nameAlreadyExists = validateCoaName(getValues("name"));

            if (nameAlreadyExists) {
                setError("name", {
                    message: "Chart of accounts name is already in use",
                });
            } else {
                await onCoASubmit({ ...getValues() }, coaId);
                if (coaId !== undefined) {
                    navigateToChartOfAccountsEditDetails(coaId);
                }
            }
        }, [
            coaId,
            getExcludedCoANames,
            onCoASubmit,
            navigateToChartOfAccountsEditDetails,
            validateCoaName,
            getValues,
            setError,
        ]);

        // Expose `isDirty` + `onConfirm()` via ref
        useImperativeHandle(
            ref,
            () => ({
                isDirty,
                onConfirm,
            }),
            [isDirty, onConfirm],
        );

        // NEW: Notify parent whenever isDirty changes
        useEffect(() => {
            onDirtyChange?.(isDirty);
        }, [isDirty, onDirtyChange]);

        return (
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "0.5rem",
                    width: "500px",
                    margin: "80px auto",
                    ...(sx || {}),
                }}
                {...props}
            >
                {isError ? (
                    <LayoutFillParentCentered>
                        <ErrorMessage title="Error loading chart of accounts">
                            There was an error. Please try refreshing the page.
                        </ErrorMessage>
                    </LayoutFillParentCentered>
                ) : (
                    <>
                        <Dropdown
                            label="Owner Company"
                            placeholder={
                                getCompaniesState === "pending" || isLoading
                                    ? "Loading Company data..."
                                    : "Select owner company"
                            }
                            options={companyOptions}
                            value={watch("companyId") ?? null}
                            onChange={value => {
                                setValue("companyId", value, {
                                    shouldValidate: true,
                                });
                                void trigger("companyId");
                            }}
                            disabled={
                                getCompaniesState === "pending" ||
                                isLoading ||
                                !coaIsDraft
                            }
                            sx={{
                                caretColor: "transparent",
                            }}
                        />
                        {errors.companyId && (
                            <Box
                                component="span"
                                sx={{
                                    color: "red",
                                    fontSize: "0.875rem",
                                }}
                            >
                                {errors.companyId.message}
                            </Box>
                        )}

                        <TextField
                            label="Chart of Accounts Name"
                            placeholder="Chart of Accounts Name"
                            disabled={isLoading || !coaIsDraft}
                            {...register("name", {
                                required: "Name is required",
                                validate: (value: string) =>
                                    validateCoaName(value)
                                        ? "Chart of accounts name is already in use"
                                        : true,
                            })}
                        />

                        {errors.name && (
                            <Box
                                component="span"
                                sx={{
                                    color: "red",
                                    fontSize: "0.875rem",
                                }}
                            >
                                {errors.name.message}
                            </Box>
                        )}

                        <TextField
                            label="Chart of Accounts Description"
                            placeholder="Chart of Accounts Description"
                            disabled={isLoading || !coaIsDraft}
                            {...register("description")}
                        />

                        <Checkbox
                            checked={watch("includeSubcategories")}
                            disabled={
                                isLoading ||
                                !coaIsDraft ||
                                coaContainsSubcategories
                            }
                            onChange={e =>
                                setValue(
                                    "includeSubcategories",
                                    e.target.checked,
                                    { shouldDirty: true },
                                )
                            }
                            label="Include Subcategories"
                            value="include-subcategories"
                            data-testid={testIds.checkboxIncludeSubcategories}
                        />

                        <Checkbox
                            checked={watch("includeNonOperatingAccounts")}
                            disabled={
                                isLoading ||
                                !coaIsDraft ||
                                coaContainsNonOperatingAccounts
                            }
                            onChange={e =>
                                setValue(
                                    "includeNonOperatingAccounts",
                                    e.target.checked,
                                    { shouldDirty: true },
                                )
                            }
                            label="Include non-operating accounts"
                            value="include-non-operating-accounts"
                            data-testid={
                                testIds.checkboxIncludeNonOperatingAccounts
                            }
                        />
                    </>
                )}
            </Box>
        );
    },
);

export default EditChartOfAccountsForm;
EditChartOfAccountsForm.displayName = "EditChartOfAccountsForm";
